import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = unauthenticated, object = user
    const [error, setError] = useState("");

    const fetchMe = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(false);
        }
    }, []);

    useEffect(() => {
        fetchMe();
    }, [fetchMe]);

    const login = async (email, password) => {
        try {
            setError("");
            const { data } = await api.post("/auth/login", { email, password });
            if (data.access_token) localStorage.setItem("erp_access_token", data.access_token);
            setUser(data);
            return true;
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            return false;
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {}
        localStorage.removeItem("erp_access_token");
        setUser(false);
    };

    const hasPerm = (module, action = "read") => {
        if (!user || user === false) return false;
        if (user.role === "admin") return true;
        const perms = user.permissions || {};
        return (perms[module] || []).includes(action);
    };

    // Granular report sub-permission check.
    // If user has no `report_permissions` configured at all, fall back to the
    // legacy module-level `permissions.reports` check so existing users keep
    // seeing every report until an admin opts them into granular mode.
    const hasReportPerm = (key, action = "read") => {
        if (!user || user === false) return false;
        if (user.role === "admin") return true;
        const rp = user.report_permissions || {};
        if (rp && Object.keys(rp).length > 0) {
            return (rp[key] || []).includes(action);
        }
        const legacy = (user.permissions || {}).reports || [];
        return legacy.includes(action);
    };

    // Returns null (unrestricted) or a Set of allowed entity ids.
    const allowedEntitySet = () => {
        if (!user || user === false) return null;
        if (user.role === "admin") return null;
        const ids = user.allowed_entity_ids || [];
        return ids.length ? new Set(ids) : null;
    };

    return (
        <AuthContext.Provider value={{ user, login, logout, error, refresh: fetchMe, hasPerm, hasReportPerm, allowedEntitySet }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
